import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { unbilledArLinkFromSettings } from './engines/billingTerms';   // billing terms P2: the Unbilled-AR link (p), 8th builder arg
import { getCompanies, getDefrevData, getEmployeesWithActuals, getVendorsWithActuals, getFixedAssets, getIntangibleAssets, getPrepaidContracts, getCompanySettings, updateCompanySettings, getFundraisingRounds, getAEQuotaSettings, getCompanyQuotaSettings, getMiscRevenueLines, getImplementationLines, getFederalTaxRates, getStateTaxRates, getCompanyStateSuta, getGLLineMappings, getRevenueModuleMappings, getBSAssumptions, getOnboardingState, getDepartmentMappings, getHistoricalGLLines, getDrLineReservations } from './api';
import { runModel } from './engines/orchestrator';
import { daStartFromOnboardingState } from './utils/daStartConvention';
import { runFullModel } from './engines/runFullModel';
import { preflightCheck } from './engines/preflightCheck';
import { buildAllMonths, subtractOneMonth } from './engines/dateUtils';
import { buildISLinesForCompany } from './engines/buildISLines';
import { commissionLineKeys as commissionLineKeysOf } from './utils/perDgPayrollActuals';   // commission ship 2c: the one qualifying set
import { buildBSLinesForCompany } from './engines/buildBSLines';
import { buildCFSLinesForCompany } from './engines/buildCFSLines';
import { injectDynamicBSLines, injectDynamicCFSLines } from './engines/dynamicLines';
import { moduleIsWired } from './engines/moduleWiring';
import { payScheduleFromSettings } from './engines/paySchedule';
import { commissionAccrualFromSettings } from './engines/commissionAccrual';   // commission ship 4: the ONE mapper settings row -> the accrual answer   // bi-weekly payroll, step 3b: the ONE mapper settings row -> schedule
import { overlayLeafLabels } from './utils/leafLabels';

/* ═══════════════════════════════════════════════════════════════════════════════
   MODEL PROVIDER
   ──────────────
   Owns the full financial model state. Runs the orchestrator (IS → BS → CFS
   with iterative sweep convergence) and distributes results to all pages.

   Input modules call markStale() after saving changes.
   Output modules call ensureFresh() on mount — triggers re-run if stale.
   Config changes (cutoff, BS assumptions, sweep settings) trigger immediate re-run.
   ═══════════════════════════════════════════════════════════════════════════════ */

const ModelContext = createContext(null);
export const useModel = () => useContext(ModelContext);

/* ★ 2026-08-12 — DEFAULT_BS_ASSUMPTIONS is DELETED (was ~40 lines of Semitek's own
   chart: chase_money_market, ar_stripe, ar_chargebee, deferred_discount, …). It was
   already DEAD: declared once, carried an explicit eslint-disable for no-unused-vars,
   and a repo-wide grep found ZERO readers. Its stated justification was "a fallback if
   the DB fetch fails" and "migration scripts mirror it" — the first never existed in
   code, and the second (server/bs-assumptions-base.js + the two seed scripts) is
   deleted in the same commit along with the tenants it seeded. Phase 6 has loaded
   bs_assumptions per-company from the DB since 97c577a. See diffs/step4b-deadcode.txt. */

/* W-025: companyName / pageLabel are passed by App.js only so a "could not be calculated" message can name the page and the company in
   its copyable details block. They feed no calculation. */
/* W-053 step 3b: canEditModel = canEditModel(user) (utils/audience.js), passed by App.js. false for a view-only login: the two
   persisting config setters below do nothing for it, and every page / modal reads it from this context to grey its edit regions. */
/* Commission ship 5 (b), Brian 10-10: superAdmin = isSuperAdmin(user) (utils/audience.js, the ONE audience predicate), passed by App.js
   beside canEditModel. false unless App.js says so (fail-closed: a tenant user's view). Display gating only -- a page reads it from this
   context, never a second role check. */
export default function ModelProvider({ companyId, children, companyName, pageLabel, canEditModel = false, superAdmin = false }) {
  // ── Model config (editable by output pages) ──
  // ★ SWEEP B3b: was `useState("2025-12")` — SEMITEK'S close month as the initial
  // cutoff for EVERY tenant, and the LIVE source of the seam for any company with a
  // NULL companies.close_month (cid=1/cid=2 were exactly that until 3b's backfill).
  // initialCutoff seeds from companies.close_month below; all three tenants now carry
  // one, and a company that genuinely has none gets null -> runFullModel's fail-loud
  // entry guard (a named missingCloseMonth record), never another company's calendar.
  const [cutoff, setCutoff] = useState(null);
  // Phase 6: starts empty — fetchAndCompute populates from the bs_assumptions
  // DB table. Empty {} is safe; computeBS treats missing entries as zeros.
  const [bsAssumptions, setBsAssumptions] = useState({});
  // Build 1 (item 4, Option B): honest-null default. The old {minCash:250000, rate:0.038}
  // were SEMITEK'S values (B3-4) — a tenant with NULL sweep columns silently inherited them.
  // Now a null DB column flows through as null (the :759-761 seed falls back to THIS default),
  // so the unset state is VISIBLE to the Step-9 block + the PSWEEP pre-flight item instead of a
  // masked Semitek number. mround keeps 'none' (the common default, not a Semitek-specific value).
  const [sweepSettings, setSweepSettings] = useState({ minCash: null, mround: "none", rate: null });

  // ── Model outputs ──
  const [isData, setIsData] = useState({});
  const [payrollHC, setPayrollHC] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const [contractorHC, setContractorHC] = useState(null);
  const [bsData, setBsData] = useState({});
  const [cfsData, setCfsData] = useState({});
  const [swData, setSwData] = useState({});
  const [iterations, setIterations] = useState(0);
  const [converged, setConverged] = useState(false);
  const [frLines, setFrLines] = useState([]);
  const [implDeferredLines, setImplDeferredLines] = useState([]);
  const [aeQuotaDR, setAEQuotaDR] = useState(null);
  const [companyQuotaDR, setCompanyQuotaDR] = useState(null);
  const [miscRevenue, setMiscRevenue] = useState({});
  const [miscRevenueLines, setMiscRevenueLines] = useState([]);
  const [implementationRevenue, setImplementationRevenue] = useState({});
  const [implementationLines, setImplementationLines] = useState([]);
  // ★ Item-5 READER (walk-minimal) — whole-model empty state. A single derived gap
  // (null when clear) covering the engine's fail-loud records + the no-horizon
  // fetch-path early return (DEFECT-A hang). Purely additive capture: a plain
  // useState setter alongside the sibling setIsData/etc. calls — it triggers a
  // re-render only, never a re-fetch/recompute, and touches NONE of the
  // switch-race gates (cutoffSeededRef / inFlightRef / staleRef / latch).
  const [onboardingGap, setOnboardingGap] = useState(null);
  // 9d landing 1: the REPORT-ONLY pre-flight verdict, published for the Go Live gate.
  // null = not yet computed (or the model has not built) — the gate MUST treat null as
  // NOT-PASSED, never as "no failures found". An absent verdict is not a clean one.
  const [preflight, setPreflight] = useState(null);

  // ── Raw data from DB (held for orchestrator re-runs on config changes) ──
  const rawRef = useRef({
    isActuals: {}, isForecast: {},
    bsActuals: {}, bsForecast: {},
    dec23: {}, sweepAct: {}, oneoffs: {}, finZero: {},
    employees: [], vendors: [], fixedAssets: [], intangAssets: [],
    daResult: { dep: {}, amort: {}, isLines: {} },
    mergedForecast: {},
  });

  // ── Control state ──
  // ★ CRASHROOT PART 1 — was `useState(false)`. `loading` was FALSE FROM MOUNT and
  // stayed false across the ENTIRE initial-load effect, which awaits getCompanies()
  // before fetchAndCompute is ever entered — a network round trip, not a frame, in
  // which `loading === false` was simply a lie (measured: diffs/crashroot-phase0.txt
  // §1.B). Behaviourally INERT on every current consumer: all four already AND it
  // with a condition that is false at mount, so this repairs the CONTRACT, not the
  // pixels. It does NOT on its own mean data-ready — see modelState below.
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  /* ★★ CRASHROOT PART 1 — `modelState`: the ONE owner of "is the model ready",
     decided by WHICH TERMINAL EXIT RAN, never by re-measuring allMonths.
       'loading'  no terminal exit reached yet (initial mount, or a run in flight)
       'ready'    the success exit, with no onboarding gap recorded
       'empty'    computed and HONESTLY EMPTY — the no-horizon early return, or an
                  engine fail-loud record (missingCloseMonth / missingChartOfAccounts)
       'error'    the fetch/compute THREW
     ⛔ WHY NOT `allMonths.length > 0`: [] is AMBIGUOUS between "never ran" and "ran,
        and there is genuinely no window". A flag that cannot tell those apart trades
        a blank page for a HUNG SPINNER — which is exactly what the 08-31 crash sweep
        shipped: on a thrown fetch every guarded page spun forever, reproduced on the
        pre-fix tree in diffs/_crashroot-verifyA-pre.txt.
     ★ EVERY WRITE BELOW SITS BESIDE AN EXISTING setOnboardingGap, OR IN THE CATCH, so
        the two can never disagree about the same run.
     ⓘ ADDITIVE: no existing context key changes meaning. The remaining six pages are
        switched over separately (Mantra #6 — scaffolding, then switchover). */
  const [modelState, setModelState] = useState('loading');
  const [modelError, setModelError] = useState(null);
  /* W-025: WHY the model is in 'error' -- { kind, trigger, at }. kind 'load' = the fetch failed (network / server); kind 'compute' =
     the data arrived and the model threw, on the load or on a recalculation. ModelLoadError reads it; null when there is no error. */
  const [modelErrorInfo, setModelErrorInfo] = useState(null);
  const staleRef = useRef(true);
  // ★★ TENANT-SWITCH RACE FIX (diffs/switch-bug-phase0.txt).
  //
  // <ModelProvider key={companyId}> REMOUNTS on a company switch, so cutoff resets to
  // its useState(null). Two things then race to compute: a page's mount `ensureFresh()`
  // — which fires FIRST, because React runs effects child-before-parent — and the
  // initial-load effect below, which seeds cutoff from companies.close_month only AFTER
  // an awaited getCompanies(). The page's run therefore reached runFullModel with a NULL
  // cutoff, hit its missingCloseMonth guard, produced an EMPTY model, and then LATCHED
  // it (staleRef=false + setInitialized(true)), so nothing ever recomputed: a blank page
  // until a manual reload. Pre-3b the same race ran with useState("2025-12") and
  // produced a populated-but-wrong-seam model, which is why it went unnoticed.
  //
  // cutoffSeededRef: "the seed attempt has RESOLVED" — set unconditionally just before
  // the initial-load compute, whether close_month existed, was null, or the fetch threw.
  // ensureFresh refuses to compute before that, leaving staleRef TRUE so it retries.
  // inFlightRef: the initial-load compute OWNS the mount; without this, the re-render
  // caused by setCutoff/setSweepSettings changes ensureFresh's identity, re-fires the
  // page effect while staleRef is still true, and starts a SECOND (correct but
  // redundant) run — exactly one compute per switch is the goal.
  const cutoffSeededRef = useRef(false);
  const inFlightRef = useRef(false);

  // ── Fetch all data and run engines ──
  const fetchAndCompute = useCallback(async (config) => {
    const { cut, bsAssum, swSet } = config;
    inFlightRef.current = true;
    setLoading(true);
    // ★ CRASHROOT: a re-run re-enters 'loading' — and clears a previous 'error', so a
    // markStale()/ensureFresh() cycle is not stuck reporting a stale failure.
    setModelState('loading');
    setModelErrorInfo(null);
    // W-025: 'fetch' until the last awaited network call has returned; a throw after that is the MODEL's (kind 'compute').
    let phase = 'fetch';
    try {
      const [defrevRes, empRes, vendorRes, fixedRes, intangRes, prepaidRes, settingsRes, fundraisingRes, aeqSettingsRes, cqSettingsRes, miscRevLinesRes, implLinesRes, fedTaxRes, stateTaxRes, companyStateSutaRes, glMappingsRes, revMappingsRes, bsAssumRes, onboardingStateRes, deptMappingsRes, histGlRes, drResvRes] = await Promise.all([
        getDefrevData(companyId),
        getEmployeesWithActuals(companyId),
        getVendorsWithActuals(companyId),
        getFixedAssets(companyId),
        getIntangibleAssets(companyId),
        getPrepaidContracts(companyId),
        getCompanySettings(companyId),
        getFundraisingRounds(companyId).catch(() => ({ data: [] })),
        getAEQuotaSettings(companyId).catch(() => ({ data: {} })),
        getCompanyQuotaSettings(companyId).catch(() => ({ data: {} })),
        getMiscRevenueLines(companyId).catch(() => ({ data: [] })),
        getImplementationLines(companyId).catch(() => ({ data: [] })),
        getFederalTaxRates().catch(() => ({ data: [] })),
        getStateTaxRates().catch(() => ({ data: [] })),
        getCompanyStateSuta(companyId).catch(() => ({ data: [] })),
        getGLLineMappings(companyId).catch(() => ({ data: [] })),
        getRevenueModuleMappings(companyId).catch(() => ({ data: [] })),
        getBSAssumptions(companyId).catch(() => ({ data: {} })),
        getOnboardingState(companyId).catch(() => ({ data: {} })),
        getDepartmentMappings(companyId).catch(() => ({ data: [] })),
        /* P13: the raw workbook history — the Step-4b review bridge's own input. The ONLY
         * consumer is the pre-flight; no engine reads it, so this cannot move a number.
         * ⚠ The [] on failure is NOT "treat it as clean". P13 SKIPS on an empty list rather
         * than judging, because a bridge run without its inputs produces a plausible WRONG net
         * income ($12.6M off with empty mappings, measured) and would FALSE-BLOCK Go Live. */
        getHistoricalGLLines(companyId).catch(() => ({ data: [] })),
        /* W-081 part 2: the Step-7 reservations (companies.dr_line_reservations). A failed read stays UNDEFINED, so P20
         * declares itself "not evaluated" rather than reading "nothing reserved". No engine reads it. */
        getDrLineReservations(companyId).catch(() => ({ data: undefined })),
      ]);

      // ── Build tax config from DB rows ──
      const fedRows = fedTaxRes.data || [];
      const stateRows = stateTaxRes.data || [];
      const fedByYear = {};
      fedRows.forEach(r => { fedByYear[r.tax_year] = r; });
      const fed2025 = fedByYear[2025] || {};
      const fed2026 = fedByYear[2026] || fed2025;
      const taxConfig = {
        ss: {
          rate: parseFloat(fed2026.ss_rate) || 0.062,
          cap: parseFloat(fed2025.ss_cap) || 176100,
          cap2026: parseFloat(fed2026.ss_cap) || 184500,
        },
        med: {
          rate: parseFloat(fed2026.med_rate) || 0.0145,
          cap: fed2026.med_cap == null ? null : parseFloat(fed2026.med_cap),
          cap2026: null,
        },
        futa: {
          rate: parseFloat(fed2026.futa_rate) || 0.006,
          cap: parseFloat(fed2026.futa_cap) || 7000,
          cap2026: parseFloat(fed2026.futa_cap) || 7000,
        },
        /* ★★ PART A1 — THE PER-YEAR FEDERAL ROWS, CARRIED THROUGH INSTEAD OF DISCARDED.
           `fedByYear` was BUILT four lines above and then COLLAPSED into fed2025/fed2026
           and thrown away, so computePayroll had no way to resolve a cap by the month's
           calendar year and selected by ACTUALS-vs-FORECAST instead. The endpoint already
           returns EVERY year (`ORDER BY tax_year`, no WHERE) and api.js already asks for
           all of them, so this is an addition — ⛔ NOT a fetch change.
           ⛔ THIS IS NOT A SECOND SOURCE. `fed2025`/`fed2026` above are THEMSELVES read out
           of `fedByYear`, so the buckets and this map cannot disagree — they are one source
           exposed two ways. The buckets stay because ScenariosPage still reads them (see
           the note on `ss`/`futa` below); the ENGINE now reads only this.
           ⓘ Shape: { [tax_year]: { tax_year, ss_rate, ss_cap, med_rate, med_cap,
                                    futa_rate, futa_cap } } — the raw rows, unparsed. */
        fedByYear,
        suta: {},        // flat map for the latest year — key set for the state
                         // dropdowns (PayrollPage stateOptions, Scenarios new-hire)
                         // + rate/cap for the read-only TaxConfig cards.
        sutaByYear: {},  // { <year>: { <ST>: { rate, cap, rateRangeLow, rateRangeHigh, isOverride, globalRate, globalCap, capIsOverride, acknowledged } } }
      };
      // Build 2a: group global state rows by year, then overlay this tenant's
      // experience-rated overrides (company_state_suta). Per state per year:
      //   rate = override.suta_rate ?? global.suta_rate   (may be null → engine fails loud)
      //   cap  = override.suta_cap  ?? global.suta_cap     (cap always non-null from global)
      //   isOverride = the override supplied the rate.
      const overrideRows = companyStateSutaRes.data || [];
      const overrideByState = {};
      overrideRows.forEach(o => {
        overrideByState[o.state_code] = {
          rate: o.suta_rate == null ? null : parseFloat(o.suta_rate),
          cap: o.suta_cap == null ? null : parseFloat(o.suta_cap),
          hasRate: o.suta_rate != null,
        };
      });
      stateRows.forEach(r => {
        const yr = parseInt(r.tax_year, 10);
        if (!taxConfig.sutaByYear[yr]) taxConfig.sutaByYear[yr] = {};
        const gRate = r.suta_rate == null ? null : parseFloat(r.suta_rate);
        const gCap = r.suta_cap == null ? null : parseFloat(r.suta_cap);
        const ov = overrideByState[r.state_code];
        const rate = ov && ov.hasRate ? ov.rate : gRate;
        const cap = ov && ov.cap != null ? ov.cap : gCap;
        taxConfig.sutaByYear[yr][r.state_code] = {
          rate, cap,                       // effective (override ?? global) — UNCHANGED merge math
          rateRangeLow: r.rate_range_low == null ? null : parseFloat(r.rate_range_low),
          rateRangeHigh: r.rate_range_high == null ? null : parseFloat(r.rate_range_high),
          isOverride: !!(ov && ov.hasRate),
          // Build 2b-1 (ADDITIVE — no effect on engine, which reads only rate/cap):
          globalRate: gRate,               // published new-employer default underneath (per year)
          globalCap: gCap,                 // statutory wage-base default (per year)
          capIsOverride: !!(ov && ov.cap != null),
          acknowledged: !!ov,              // an override ROW exists for (company,state) — year-invariant
        };
      });
      // Flat taxConfig.suta = latest year's map (for the dropdowns' key set + the
      // TaxConfig cards' rate/cap display). Falls back to {} if no rows.
      const sutaYearsAvail = Object.keys(taxConfig.sutaByYear).map(Number).sort((a, b) => a - b);
      const latestSutaYear = sutaYearsAvail.length ? sutaYearsAvail[sutaYearsAvail.length - 1] : null;
      taxConfig.suta = latestSutaYear != null ? taxConfig.sutaByYear[latestSutaYear] : {};

      phase = 'compute';   // W-025: every network call above has returned

      const d = defrevRes.data || {};
      const commissionDelayMonths = parseInt(settingsRes.data?.commission_delay_months) || 0;
      // ★ COMP-CYCLE (item #1): company-level bonus/commission-anchor derivation policy.
      // null/absent → the engine's own default applies (derive off if unknown).
      const compCycleBasis = settingsRes.data?.comp_cycle_basis || null;
      // ★ Payout-timing rework: the BONUS payout offset (0/1/2), parallel to commissionDelayMonths.
      const bonusPayoutDelayMonths = parseInt(settingsRes.data?.bonus_payout_delay_months) || 0;
      /* BI-WEEKLY PAYROLL, STEP 3b: the company's pay schedule -- the six settings fields through the ONE mapper -- and the
         account it links (the builder wires that balance-sheet line to the engine's accrued-payroll balance). A company that
         was never asked (all five today) maps to a schedule whose frequency is null, which every reader treats as
         semi-monthly. BOTH are kept on rawRef and passed in BOTH places a model is run (runFullModel's cfg AND
         runOrchestrator's hand-built cfg -- CLAUDE.md 9.5) and to runScenario. */
      const paySchedule = payScheduleFromSettings(settingsRes.data);
      const accruedPayrollLineKey = settingsRes.data?.accrued_payroll_bs_line_key || null;
      /* COMMISSION SHIP 4: how the books treat commissions -- the three settings fields through the ONE mapper. Never asked (all
         five today) -> books null, read as today's timing. Kept on rawRef and passed to the BS builder (7th argument), runFullModel,
         the pre-flight and runScenario -- the same places the pay schedule goes. */
      const commissionAccrual = commissionAccrualFromSettings(settingsRes.data);

      // Phase 8: gate the Semitek-legacy defrev architecture. When
      // legacy_defrev_enabled=false (the new-tenant default), runFullModel
      // skips computeDefRev + computeRevenue entirely and the consolidated-
      // revenue overlay is suppressed. Semitek + Chris Test seeded with
      // true/'rev_stripe' to preserve byte-identity. Resolves the Phase 2b
      // transitional debt.
      const legacyDefrevEnabled = settingsRes.data?.legacy_defrev_enabled === true;
      /* ★ P4-b — `useLegacyDeptFallback` and `consolidatedRevenueTargetLine` are no longer
       * derived: nothing consumes them any more. `legacyDefrevEnabled` above SURVIVES,
       * because runFullModel's fail-loud throw still reads it — the COLUMNS all remain in
       * the DB and in server.js's SELECT; only the client plumbing is gone. */
      const companyName = settingsRes.data?.name || '';

      // Sub-phase 2 (page sweep): per-tenant date window sourced from DB.
      //   historicalStart ← onboarding_state.historical_start, fallback '2024-01'
      //     for tenants that predate the onboarding flow (Semitek, Chris Test).
      //   forecastEnd ← companies.forecast_through (nullable since Build 1b —
      //     server/migrate-drop-forecast-through-notnull.js). A company that has
      //     not completed Step 1 has NO horizon; we render an honest-empty model
      //     rather than a silent Semitek-shaped '2026-12' guess (B3-4's class).
      const obState = (onboardingStateRes && onboardingStateRes.data) || {};
      // ★ SWEEP B3b: was `|| '2024-01'` — Semitek's historical start, LIVE for any
      // tenant with no onboarding_state row (cid=1/cid=2 had none until 3b's backfill).
      // Null now flows to the fail-loud entry guard instead of a foreign window.
      const historicalStart = obState.historical_start || null;
      const forecastEnd = (settingsRes && settingsRes.data && settingsRes.data.forecast_through) || null;
      if (!forecastEnd) {
        // Honest-empty (Build 1b, ruling (ii)). Unreachable in the live flow —
        // is_onboarded gates the nav switcher, so ModelProvider only mounts for a
        // tenant past Step 8, by which point Step 1 (which requires forecast_through)
        // has written it. If forced, render empty, never a guess. The `return`
        // runs the try's finally → setLoading(false); rawRef stays at its safe
        // initial state.
        console.warn(`[ModelProvider] company ${companyId}: no forecast horizon (companies.forecast_through is null) — Step 1 not complete; model window empty.`);
        // ★ Item-5 READER — DEFECT-A fix: name the gap so the pages show a message
        // instead of a permanent spinner (allMonths stays [] on this path, so the
        // pages' `!allMonths.length` guard would otherwise hold the spinner forever).
        setOnboardingGap({ kind: 'no_horizon' });
        // ★ CRASHROOT — TERMINAL EXIT S2: computed, honestly empty. Named, not spun.
        setModelState('empty');
        // Rider (filed null-horizon DEFECT B): mark the fetch "done" so ensureFresh's
        // `if (!staleRef.current && initialized) return` short-circuits — otherwise the
        // model never marks itself fresh and re-runs fetchAndCompute on every mount
        // (repeated network fetch + re-compute + re-warn). DEFECT A (the named-empty
        // render guard, so the page shows a message instead of a permanent spinner)
        // stays filed.
        staleRef.current = false;
        if (!initialized) setInitialized(true);
        return;
      }
      const ALL_M_36 = buildAllMonths(historicalStart, forecastEnd);
      /* ★★ CRASHROOT — THE SAME "ONE LAYER SHORT" DEFECT runFullModel:170-175 RECORDS
         FIXING FOR ITSELF, WHICH WAS NEVER FIXED ONE LEVEL UP HERE. buildAllMonths'
         own Build-1b guard correctly returns [] for a null historicalStart, and the
         very next line then dereferenced ALL_M_36[0] -> subtractOneMonth(undefined)
         -> TypeError. MEASURED on the pre-fix tree (diffs/_crashroot-verifyA-pre.txt
         + the S4 probe): the empty-window case NEVER REACHED runFullModel's named
         'no_model_window' branch at all — it threw into the catch below, so the
         engine's fail-loud record was UNREACHABLE FROM THIS FILE and the four pages
         that read onboardingGap showed a spinner forever.
         ⇒ Guarded, so an empty window flows to the engine's NAMED, HANDLED state
           (fail-loud means a named state, never a TypeError — §12.5 rule 7).
         §E0: on any non-empty window `ALL_M_36.length` is truthy and this is the
         identical call, so no tenant with a real window moves by a cent. */
      const openingMonth = ALL_M_36.length ? subtractOneMonth(ALL_M_36[0]) : null;
      const employees = (empRes.data || []).map(e => {
        // Infer termDate from monthlyData (same as PayrollPage) — more reliable than end_date alone
        const md = e.monthlyData || {};
        const activeMs = ALL_M_36.filter(m => (md[m] || 0) > 0);
        const lastActive = activeMs.length ? activeMs[activeMs.length - 1] : null;
        const hasTerminated = lastActive && lastActive < ALL_M_36[ALL_M_36.length - 1];
        let termDate = null;
        if (hasTerminated && lastActive) {
          termDate = lastActive + "-28"; // day doesn't matter, only month is used for gating
        } else if (e.end_date && typeof e.end_date === 'string') {
          termDate = e.end_date.slice(0,10);
        }
        return {
        ...e,
        salary: parseFloat(e.annual_salary) || 0,
        startDate: e.start_date ? (typeof e.start_date === 'string' ? e.start_date.slice(0,10) : null) : null,
        termDate,
        benefitsStartDate: e.benefits_start_date ? (typeof e.benefits_start_date === 'string' ? e.benefits_start_date.slice(0,10) : null) : null,
        // ★ C-11 (shape A): the engine reads benefit_components directly (Σ its values). The 4 typed
        // columns are dead. The gate (benefitsStartDate || startDate) is above — unchanged.
        benefitComponents: e.benefit_components || {},
        raises: (e.raises || []).map(r => ({
          salary: r.salary,
          effectiveDate: typeof r.effectiveDate === 'string' ? r.effectiveDate.slice(0,10) : r.effectiveDate,
        })),
      };});
      const vendors = (vendorRes.data || []).map(v => ({
        ...v,
        // ★ Item 8: HONEST-NULL. This was `|| "ga_software"` — Semitek's own G&A
        // software leaf promoted to "the default vendor category" pre-refactor
        // (a B3-4 value-not-default; cid=3 has ZERO ga_software rows, so it was a
        // ghost key there). A vendor with no is_line now carries none: P5 names it
        // (BLOCKING) instead of the fallback quietly booking its spend into a
        // Semitek line (or, on a tenant without that leaf, dropping it to $0 while
        // P5 reported a key the operator never chose).
        isCat: v.is_line || null,
        active: v.is_active !== false,
        monthlyData: v.monthlyData || {},
      }));
      const fixedAssets = (fixedRes.data || []).map(a => ({
        ...a,
        date: a.purchase_date,
        life: parseFloat(a.useful_life_years) || 0,
        salvage: parseFloat(a.salvage_value) || 0,
        cost: parseFloat(a.cost) || 0,
        method: a.depreciation_method || "SL",
        // Per-asset-type routing (Cp2): the asset STORES the BS leaf it rolls onto.
        // computeDA keys grossAdditions off this id alone — no guessing, no fallback.
        bs_line_id: a.bs_line_id ?? null,
        /* ★ B5 s3a — THE FIXED/INTANGIBLE SUB-TYPE, STAMPED AT THE FETCH BOUNDARY.
         * Which of the two asset endpoints a row came from IS its sub-type: the operator
         * chose it when they used the fixed-asset Add button, or the template's FA/IA
         * column, or the sheet's START_FIXED marker (§E1 — an explicit operator choice,
         * never a name guess). Deriving it HERE rather than storing a DB column is
         * deliberate: a stored column would hold the same constant on every row of the
         * table and could then DISAGREE with it, which is the §6 divergence B5 exists to
         * remove. Re-derived on every fetch, it cannot go stale.
         * ⓘ s3a only STAMPS and ASSERTS. computeDA still splits depreciation from
         *   amortization BY ARGUMENT POSITION; s3b makes it read this field instead. */
        sub_type: 'fixed_assets',
      }));
      const intangAssets = (intangRes.data || []).map(a => ({
        ...a,
        date: a.purchase_date || a.start_date,
        life: parseFloat(a.useful_life_years) || 0,
        salvage: parseFloat(a.salvage_value) || 0,
        cost: parseFloat(a.cost) || 0,
        method: a.amortization_method || a.depreciation_method || "SL",
        bs_line_id: a.bs_line_id ?? null,
        // ★ B5 s3a — the sub-type, stamped from the endpoint this row came from. See the
        // fixed-asset map above for why this is derived here and not stored in the DB.
        sub_type: 'intangible_assets',
      }));

      const aeqSettings = aeqSettingsRes.data || {};
      const cqSettings = cqSettingsRes.data || {};
      const miscLines = miscRevLinesRes.data || [];
      const implLines = implLinesRes.data || [];
      // W-081 part 2: undefined = the read failed (P20 not evaluated); null = nothing reserved.
      const drLineReservations = (drResvRes && drResvRes.data !== undefined) ? ((drResvRes.data && drResvRes.data.reservations) || null) : undefined;
      const prepaidStartBal = parseFloat(settingsRes.data?.prepaid_start_balance) || 0;
      /* companies.sweep_interest_is_line_key — three states in one column:
         NULL = never answered · 'oi_interest' = the operator chose to keep a separate
         forecast line · any other key = the historical IS leaf to book the sweep's
         forecast interest onto. Honest-null: no inference here (§E1). */
      const sweepInterestLineKey = settingsRes.data?.sweep_interest_is_line_key || null;
      /* D&A routing, Part 2: companies.depreciation_is_line_key / amortization_is_line_key --
         the same three states (null / the stream's sentinel / an IS leaf). Honest-null. */
      const depreciationLineKey = settingsRes.data?.depreciation_is_line_key || null;
      const amortizationLineKey = settingsRes.data?.amortization_is_line_key || null;
      /* The D&A start convention (2026-10-13): a company-wide setting stored on the onboarding-state row this fetch already
         reads (utils/daStartConvention.js is its one owner and names the key). Never stored -> undefined -> every schedule
         starts in the in-service month, exactly as before; undefined also adds no field to rawData. */
      const daStartConvention = daStartFromOnboardingState(obState);
      const fundraisingRounds = (fundraisingRes.data || []).filter(r => r.round_type === 'equity');

      // ── Build per-company IS_LINES (Phase 2a builder, Phase 5 config-aware context) ──
      // moduleContext was force-ON during Phase 2b as a transitional guard. Phase 5
      // makes it truly per-tenant by consulting revenue_module_mappings (is the
      // module wired at all?) alongside each module's settings.enabled flag.
      const glMappings = glMappingsRes.data || [];
      const revMappings = revMappingsRes.data || [];
      const deptMappings = deptMappingsRes.data || [];
      const moduleContext = {
        // AE Quota: must be wired AND not explicitly disabled (server default ON)
        aeQuotaEnabled:
          moduleIsWired(revMappings, 'ae_quota') &&
          (aeqSettings?.enabled !== false),
        // Company Quota: must be wired AND explicitly enabled (server default OFF)
        companyQuotaEnabled:
          moduleIsWired(revMappings, 'company_quota') &&
          (cqSettings?.enabled === true),
        hasFixedAssets: (fixedAssets?.length || 0) > 0,
        hasIntangibleAssets: (intangAssets?.length || 0) > 0,
        /* ★ The operator's sweep-interest IS line, carried on the CONTEXT rather than as a
         * 7th positional arg: buildISLinesForCompany has ONE production caller (below) and
         * 13 diagnostic callers, MOST of which pass only five args. Riding on ctx changes no
         * signature, and `undefined` there suppresses nothing — so every existing gate stays
         * byte-identical BY CONSTRUCTION rather than by promise. */
        sweepInterestLineKey,
        // D&A routing, Part 2 -- on the ctx for the same reason as sweepInterestLineKey.
        depreciationLineKey,
        amortizationLineKey,
        /* Commission ship 2c (R13 / R14): the *_commissions keys of the (category, department group) pairs whose uploaded IS has
         * a payroll row STORED as 'commissions' -- computed ONCE from the stored rows (the workbook lines, the Step-3 answers,
         * the departments) by the back-fill's own resolution. buildISLines emits their Commissions leaf from it, runFullModel
         * hands it to computePayroll, and runScenario reads it back from rawData.moduleContext. [] -> today. */
        commissionLineKeys: commissionLineKeysOf(histGlRes.data || [], glMappings, deptMappings),
      };
      /* ★ PART C — implementationLines as the 7th positional arg, so the BUILDER emits the
       * rev_impl_<id> leaf instead of IncomeStatementPage splicing its own at render time.
       * ⓘ The note above chose `ctx` for sweepInterestLineKey to avoid growing the signature.
       *   This one is positional deliberately: it is DATA (rows), like `isActuals` in slot 6,
       *   not tenant CONFIG like the ctx flags. The safety property is unchanged either way —
       *   the param is optional and every diagnostic that omits it passes `undefined`, which
       *   the builder reads as no lines and therefore emits no leaf. Byte-identical BY
       *   CONSTRUCTION for all ~23 existing call sites, not by promise. */
      /* W-089: the misc lines as the 8th positional arg, so the BUILDER emits an unrouted line's rev_misc_<id> leaf. */
      const isLines = buildISLinesForCompany(companyId, glMappings, revMappings, deptMappings, moduleContext, d.is_actuals || {}, implLines, miscLines);

      // Phase 3b: per-company BS_LINES + CFS_LINES from bs_actuals_meta
      // (backfilled in Phase 3a for cid 1+2; populated by onboarding Step 4 for cid 3+).
      // When meta is empty (no backfill yet), builders fall back to the synthetic
      // skeleton with no leaves — engines then fall back to the hardcoded BS_LINES
      // / CFS_LINES exports via their `bsLines || BS_LINES` guards.
      /* THE LABEL COMES FROM ITS OWNER, NOT FROM THE CACHE. `bs_actuals_meta[k].label`
       * is a COPY of gl_line_mappings.display_name written at Step-4 import time; this
       * overlays the owner's value (matched BY KEY, never by name) before anything reads
       * it. ★ ONE OVERLAY, FOUR READERS — buildBSLines takes leaf.label, and buildCFSLines
       * derives every CFS row label from the SAME object ('(Purchase of) X',
       * '(Increase)/Decrease X', and the sweep row's own label), so fixing the object
       * fixes all of them without moving buildBSLinesForCompany's signature. See
       * utils/leafLabels.js for why the fallback is load-bearing (the mid-wizard case). */
      const bsActualsMeta = overlayLeafLabels(d.bs_actuals_meta || {}, glMappings);
      const bsLines = Object.keys(bsActualsMeta).length > 0
        ? buildBSLinesForCompany(companyId, bsActualsMeta, revMappings, moduleContext, implLines, { schedule: paySchedule, lineKey: accruedPayrollLineKey }, commissionAccrual, unbilledArLinkFromSettings(aeqSettings))   /* billing terms P2: + the Unbilled-AR link (8th) */   // commission ship 4: + the accrued-commissions link (7th). step 3b: + the accrued-payroll link (6th). W-081: a deferred implementation line linked to its real deferred-revenue account wires that leaf
        : null;
      // cfs_oneoffs is passed so one-off CFS lines (PPP forgiveness, loan write-offs,
      // …) are emitted ONLY for tenants that actually have them — a one-off belongs
      // to the tenant that has it and never transfers.
      const cfsLines = Object.keys(bsActualsMeta).length > 0
        ? buildCFSLinesForCompany(bsActualsMeta, revMappings, moduleContext, d.cfs_oneoffs || {})
        : null;

      // Phase 6: assumptions now load from the bs_assumptions DB table.
      // bsAssum (param) is preserved as an OPTIONAL override — when the
      // user edits in the BS panel and triggers a re-run, handleSet*
      // passes the React state through. On fresh fetch / company switch,
      // bsAssum arrives as the React state from the previous tenant which
      // is stale; in that case prefer the DB-loaded values.
      const dbAssumptions = bsAssumRes.data || {};
      const loadedBsAssumptions = (bsAssum && Object.keys(bsAssum).length > 0 && Object.keys(dbAssumptions).length === 0)
        ? bsAssum                  // DB empty (new company) — fall back to passed state if any
        : dbAssumptions;           // DB has rows — DB wins on fetch
      // Sync React state so the BS panel UI shows the loaded values
      setBsAssumptions(loadedBsAssumptions);

      // ── Run full engine chain ──
      const fullResult = runFullModel({
        commissionLineKeys: moduleContext.commissionLineKeys,   // commission ship 2c (R14)
        employees,
        vendors,
        fixedAssets,
        intangAssets,
        aeqSettings,
        cqSettings,
        miscRevenueLines: miscLines,
        implementationLines: implLines,
        bsAssumptions: loadedBsAssumptions,
        sweepSettings: swSet,
        isActuals: d.is_actuals || {},
        isForecast: d.is_forecast || {},
        bsActuals: d.bs_actuals || {},
        bsForecast: d.bs_forecast || {},
        dec23: d.cfs_dec23 || {},
        sweepAct: d.cfs_sweep || {},
        oneoffs: d.cfs_oneoffs || {},
        finZero: d.cfs_fin_zero || {},
        fundraisingRounds,
        prepaidContracts: prepaidRes.data || [],
        prepaidStartBal,
        commissionDelayMonths,
        compCycleBasis,
        bonusPayoutDelayMonths,
        paySchedule,   // bi-weekly payroll, step 3b
        commissionAccrual,   // commission ship 4
        // ★ B3a (A4): honest-null, was Semitek's 'chase_money_market'.
        sweepAccountKey: bsLines.find(l => l.role === 'sweep_target')?.id || null,
        sweepInterestLineKey,
        depreciationLineKey,
        amortizationLineKey,
        daStartConvention,
        taxConfig,
        historicalStart,
        forecastEnd,
        isLines,
        bsLines,
        cfsLines,
        revMappings,
        deptMappings,
        legacyDefrevEnabled,
        cutoff: cut,
      });

      // ── Update React state from engine results ──
      // 5b-engine: ensure isData has zero-filled per-line maps for every
      // picker source key, so VendorDashboard overlay's isData[sourceKey]
      // lookup always finds a defined map. This aligns the overlay with
      // the engine's strict $0 semantics for picker keys whose underlying
      // module is disabled (e.g. cid=3 rev_self_serve when Company Quota
      // is not yet enabled in onboarding). Purely additive — does not
      // affect any engine computation or chart-of-accounts output.
      // Resolves §12 "VendorDashboard overlay strict-fallback alignment".
      const pickerSourceKeys = new Set();
      (revMappings || []).forEach(r => {
        if (r.module_type && r.module_type !== 'skip' && r.gl_line_key) {
          pickerSourceKeys.add(r.gl_line_key);
        }
      });
      const enrichedIsData = { ...fullResult.isData };
      for (const key of pickerSourceKeys) {
        if (!enrichedIsData[key]) {
          enrichedIsData[key] = {};
          ALL_M_36.forEach(m => { enrichedIsData[key][m] = 0; });
        }
      }
      setIsData(enrichedIsData);
      setBsData(fullResult.bsData);
      setCfsData(fullResult.cfsData);
      setSwData(fullResult.swData);
      setIterations(fullResult.iterations);
      setConverged(fullResult.converged);
      // ★ Item-5 READER — derive the whole-model gap from records the engines
      // already produced (READ-ONLY; no recompute). Clears to null on a fully-wired
      // tenant. no_close_month wins over no_chart_of_accounts (no seam ⇒ nothing).
      const gapVal =
        fullResult.missingCloseMonth?.length ? { kind: 'no_close_month' }
        : fullResult.missingChartOfAccounts?.length ? { kind: 'no_chart_of_accounts' }
        : null;
      setOnboardingGap(gapVal);
      /* ★ CRASHROOT — TERMINAL EXITS S1 and S4, off the SAME value the gap is derived
         from: the ENGINE'S OWN fail-loud record, not a measurement of the output. A
         gap here is precisely the state these pages already rendered as
         OnboardingIncomplete, so 'empty' reproduces today's behaviour exactly. */
      setModelState(gapVal ? 'empty' : 'ready');

      // ── Model-build completeness gate (REPORT-ONLY; engines/preflightCheck.js) ──
      // PURE check that what onboarding PRODUCED equals what the engine REQUIRES. It does
      // NOT block the model, throw, or gate is_engine_ready — it console.warns NAMED
      // offenders so a wizard-only tenant's silent gaps (C-1/C-2/C-3/C-10, orphaned
      // history, unwired vendors, …) surface at build time instead of being found by
      // walking into them. A fully-wired tenant logs nothing. Gating is_engine_ready on
      // pf.passed is the endgame once the C-series is green (see CLAUDE.md §12.5).
      try {
        // P7 (correctness): value-maps { [empId]: { [month]: amount } } + a roster carrying the RAW
        // end_date (not the inferred termDate — that would be circular against the ema being checked).
        const emaMap = {};
        employees.forEach(e => { emaMap[e.id] = e.monthlyData || {}; });
        const p7emps = employees.map(e => ({
          id: e.id, name: e.name, salary: e.salary, startDate: e.startDate,
          endDate: e.end_date ? String(e.end_date).slice(0, 10) : null,
          raises: e.raises,
        }));
        const histMonths = [...new Set(Object.values(d.is_actuals || {})
          .flatMap(s => (s && typeof s === 'object') ? Object.keys(s) : []))].sort();
        const pf = preflightCheck({
          isLines,
          // ★ A1 — the pre-flight sees the INJECTED skeletons, i.e. the rows the engine
          // actually valued (fundraising + implementation-deferred included). Feeding it the
          // pre-injection skeletons would leave P1-BS/P1-CFS structurally blind to a
          // double-count introduced by the injector — the very thing they exist to catch.
          // Injection is a no-op (same array reference) when neither family is present.
          bsLines: injectDynamicBSLines(bsLines, fullResult.frLines, fullResult.implDeferredLines),
          cfsLines: injectDynamicCFSLines(cfsLines, fullResult.frLines, fullResult.implDeferredLines),
          glMappings, rmm: revMappings, deptMappings,
          vendors, employees: p7emps, employeeMonthlyActuals: emaMap,
          isActuals: d.is_actuals || {},
          moduleSettings: {
            // C-3 (α): use the REAL row-presence flag, not Object.keys(). The AE GET fabricates a
            // full enabled:true default when no row exists, so `Object.keys(aeqSettings).length` was
            // ALWAYS truthy → moduleSettings.ae_quota was always true → the live pre-flight could
            // NEVER fire P8 for an absent AE row. The check meant to catch C-3 was blinded by the very
            // endpoint that causes C-3; a check that cannot fire reads as green, worse than no check.
            // (company_quota has the same fabrication but its GET defaults enabled:FALSE and the engine
            // skips when disabled → benign; left as-is, tracked separately.)
            ae_quota: !!(aeqSettings && aeqSettings._row_exists),
            // 9a-1: the CQ GET fabricates a default exactly like AE's did, so Object.keys()
            // was ALWAYS truthy → the live P8 could NEVER fire for an absent CQ row (the same
            // blindness C-3(α) fixed for AE, one line down — 4th "checks need the set-diff"
            // instance). Row-presence, not object-shape.
            company_quota: !!(cqSettings && cqSettings._row_exists),
            misc_revenue: miscLines.length,
            implementation: implLines.length,
          },
          // 9a-1 (P8 existence→correctness): the settings ROWS themselves — null when no real
          // row exists, so the correctness tier never evaluates a fabricated default.
          settingsRows: {
            ae_quota: (aeqSettings && aeqSettings._row_exists) ? aeqSettings : null,
            company_quota: (cqSettings && cqSettings._row_exists) ? cqSettings : null,
          },
          bsActuals: d.bs_actuals || {},
          // 9c: the misc rows themselves (the amounts tier — a routed row with all-zero
          // monthly_amounts is a row, not a configuration).
          miscLines,
          // V1.1: prepaid contracts for P11 (prepaid on a duplicate vendor name must carry a
          // resolved vendor_id — else the runtime name-fallback can silently drop the amort).
          prepaidContracts: prepaidRes.data || [],
          company: { id: companyId },   // (prepaidStartBal was P12's input only; P12 retired 2026-09-30, W-092)
          // Build 1 (item 4, PSWEEP): the RAW company sweep values (null when unset, post
          // Option-B migration) — read from the settings GET, NOT the post-fallback swSet, so
          // the check sees the true unset state. Undefined (settings fetch failed) → skipped.
          minCash: settingsRes.data ? settingsRes.data.min_cash : undefined,
          sweepRate: settingsRes.data ? settingsRes.data.sweep_rate : undefined,
          // P13: the review bridge's input, so the check can run the SAME function the wizard
          // ran and compare its net income against the model's own (isData.net_income, below).
          historicalGlLines: histGlRes.data || [],
          historicalMonths: histMonths, cutoff: cut, allMonths: ALL_M_36,
          isData: fullResult.isData, bsData: fullResult.bsData, cfsData: fullResult.cfsData,
          // P14: the payroll engine’s own result, for its orphanDeptWarnings record. ⛔ NOT
          // re-derived from `p7emps` above — that projection carries no `department` field,
          // and re-deriving would be a third copy of the department→is_category rule (§6).
          payrollResult: fullResult.payrollResult,
          // P16 (W-071): the engine results the statements were built from, so the check can hold the IS and
          // the BS to them. defRevResult carries the GATED deferred balances computeBS read.
          aeQuotaDR: fullResult.aeQuotaDR, companyQuotaDR: fullResult.companyQuotaDR,
          implementationRevenue: fullResult.implementationRevenue, mergedForecast: fullResult.mergedForecast,
          defRevResult: fullResult.defRevResult,
          // P17 (W-073 (a)): the assumption rows computeBS read -- every BS value leaf outside the overlay needs one.
          bsAssumptions: loadedBsAssumptions,
          // W-081: the implementation lines themselves -- P6 (a deferred line's balance-sheet answer), P8 (the Zero-mode trap),
          // P19 (the GAAP grid reconciliation). undefined = not supplied (those parts are then not judged).
          implementationLines: implLines,
          // W-081 part 2: the Step-7 reservations -- P20 (a reserved account no deferred implementation line links). undefined = not judged.
          drLineReservations,
          // W-138 (2026-10-05): the balance-sheet meta (owner labels overlaid) -- P8 names a deferred-revenue account no stream carries.
          bsActualsMeta,
          // Bi-weekly payroll, step 3b: P21 (a bi-weekly schedule is complete and its account is driven), P7 (re-derived under
          // the company's schedule) and P22 (the linked account's close balance vs the calendar's). undefined = not supplied.
          paySchedule, accruedPayrollLineKey,
          // Commission ship 4: P23 (the accrual answer is complete and its account is driven) and P24 (the linked account's close
          // vs the computed close). commissionReps: every sales rep's cycle, for P23 (g). undefined = not supplied.
          commissionAccrual,
          commissionReps: employees.filter(e => e.is_sales_rep).map(e => ({ id: e.id, name: e.name, commission_frequency: e.commission_frequency || null })),
        });
        if (!pf.passed || pf.warningCount) {
          const fmt = f => `  [${f.severity}] ${f.id}: ${f.what}` +
            (f.offenders.length ? `\n      offenders(${f.offenders.length}): ` +
              f.offenders.slice(0, 10).map(o => JSON.stringify(o)).join(', ') : '');
          console.warn(`[preflight] company ${companyId} — ${pf.blockingCount} blocking, ` +
            `${pf.warningCount} warning:\n` + pf.failures.map(fmt).join('\n'));
        }
        // ── 9d landing 1: EXPOSE the verdict (previously computed and thrown away) ──
        // The Go Live gate needs the SAME verdict this block already produces — full
        // P1–P11 against the INJECTED skeletons, including P9/P10 which only exist here
        // because they need computed bsData/isData. Publishing it is what keeps the gate
        // from becoming a SECOND pre-flight (Principle 0). It stays REPORT-ONLY: nothing
        // in this file reads pf.passed to block, throw, or gate is_engine_ready.
        setPreflight({ ...pf, checkedAt: new Date().toISOString() });   // W-035: when this verdict was produced
      } catch (e) {
        console.warn('[preflight] check errored (non-fatal):', e.message);
        // A thrown check must not read as a PASS to the gate — publish the failure.
        setPreflight({ passed: false, errored: true, error: e.message, blockingCount: null, warningCount: null, failures: [], checkedAt: new Date().toISOString() });
      }
      setFrLines(fullResult.frLines);
      setImplDeferredLines(fullResult.implDeferredLines);
      setPayrollData(fullResult.payrollResult);
      setPayrollHC(fullResult.payrollHC);
      setContractorHC(fullResult.contractorHC);
      setAEQuotaDR(fullResult.aeQuotaDR);
      setCompanyQuotaDR(fullResult.companyQuotaDR);
      setMiscRevenue(fullResult.miscRevenue);
      setMiscRevenueLines(miscLines);
      setImplementationRevenue(fullResult.implementationRevenue);
      setImplementationLines(implLines);

      // Save raw data for re-runs (orchestrator-only re-runs on config changes + ScenariosPage access)
      rawRef.current = {
        isActuals: d.is_actuals || {},
        bsActuals: d.bs_actuals || {},
        bsForecast: d.bs_forecast || {},
        dec23: d.cfs_dec23 || {},
        sweepAct: d.cfs_sweep || {},
        oneoffs: d.cfs_oneoffs || {},
        finZero: d.cfs_fin_zero || {},
        employees, vendors, fixedAssets, intangAssets,
        prResult: fullResult.payrollResult,
        commissionData: fullResult.commissionData,
        commissionDelayMonths,
        compCycleBasis,
        bonusPayoutDelayMonths,
        // Bi-weekly payroll, step 3b: the schedule, its linked account, and the engine's accrued-payroll balance (undefined
        // unless the company accrues bi-weekly payroll) -- runOrchestrator and runScenario read them from here.
        paySchedule,
        accruedPayrollLineKey,
        accruedPayroll: fullResult.accruedPayroll,
        // Commission ship 4: the answer and the engine's accrued-commissions balance (undefined unless the company accrues them).
        commissionAccrual,
        accruedCommissions: fullResult.accruedCommissions,
        daResult: fullResult.daResult,
        vendorOpex: fullResult.vendorOpex,
        // B6 (2026-10-27): each pct_revenue vendor's forecast as the engine books it -- the Vendor Dashboard shows this. The config
        // re-run (runOrchestrator) does not re-run the vendor engine, so it persists like vendorOpex.
        pctVendorForecast: fullResult.pctVendorForecast,
        prepaidBalance: fullResult.prepaidBalance,
        prepaidContracts: fullResult.prepaidContracts,
        prepaidStartBal: fullResult.prepaidStartBal,
        // Seam-seed tie-verdict (c1436f2) — surface the engine's authoritative
        // verdict to context so the Prepaid tab disclaimer reads it (no in-tab
        // re-derivation). Computed once by runFullModel; config re-runs
        // (runOrchestrator) don't recompute the roll, so these persist.
        prepaidTies: fullResult.prepaidTies,
        prepaidSeamResidual: fullResult.prepaidSeamResidual,
        prepaidAnchor2: fullResult.prepaidAnchor2,
        prepaidHistRollAtCutoff: fullResult.prepaidHistRollAtCutoff,
        revResult: fullResult.revResult,
        defRevResult: fullResult.defRevResult,
        isForecast: d.is_forecast || {},
        mergedForecast: fullResult.mergedForecast,
        fundraisingRounds,
        monthlyNewACV: fullResult.monthlyNewACV,
        aeQuotaDR: fullResult.aeQuotaDR,
        aeqSettings,
        companyQuotaDR: fullResult.companyQuotaDR,
        cqSettings,
        miscRevenueLines: miscLines,
        miscRevenue: fullResult.miscRevenue,
        implementationLines: implLines,
        drLineReservations,   // W-081 part 2: the Deferred Revenue page lists a reserved account first in its link picker
        implementationRevenue: fullResult.implementationRevenue,
        implLengthWarnings: fullResult.implLengthWarnings || [],
        kpiARR: fullResult.kpiARR,
        kpiARRConfig: fullResult.kpiARRConfig,
        forecastMonths: fullResult.forecastMonths,
        // ★ B3a (A4): honest-null, was Semitek's 'chase_money_market'.
        sweepAccountKey: bsLines.find(l => l.role === 'sweep_target')?.id || null,
        taxConfig,
        /* ★ P4-b — the three legacy ECHOES are removed from rawData. Nothing read them:
         * `legacyDefrevEnabled` reaches runFullModel as a PARAM (above) and the throw reads
         * THAT, not this copy. Removing them is the one shape change in this build and it is
         * declared by exact path in the A/B: three keys, `false -> undefined` twice and
         * `null -> undefined` once, per tenant. */
        allMonths: ALL_M_36,
        openingMonth,
        historicalStart,
        forecastEnd,
        companyName,
        glMappings,
        revMappings,
        deptMappings,
        moduleContext,
        sweepInterestLineKey,
        depreciationLineKey,
        amortizationLineKey,
        daStartConvention,   // read by ScenariosPage's runScenario (its runFullModel argument is an allowlist)
        isLines,
        bsActualsMeta,
        bsLines,
        cfsLines,
      };
      staleRef.current = false;
      if (!initialized) setInitialized(true);
    } catch (err) {
      console.error("ModelProvider: fetch failed", err);
      /* ★★ CRASHROOT — TERMINAL EXIT S3, THE ONE THAT HAD NO READER. This catch used
         to record NOTHING: rawRef untouched, onboardingGap null, initialized still
         false — and then the finally cleared `loading`, so every guarded page sat on
         `!allMonths.length` and SPUN FOREVER with only a console line to say why.
         Mirrors this file's own preflight rule at the pf catch below ("A thrown check
         must not read as a PASS to the gate"): a thrown FETCH must not read as "still
         loading". ⛔ NAMED ONLY — no retry is wired in this build; a browser refresh
         remounts the provider and replays the load, which is the recovery path. */
      setModelState('error');
      setModelError(err && err.message ? err.message : String(err));
      setModelErrorInfo(phase === 'compute'
        ? { kind: 'compute', trigger: 'loading the model', at: new Date().toISOString() }
        : { kind: 'load', trigger: null, at: new Date().toISOString() });
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [companyId, initialized]);

  // ── Run orchestrator with current raw data (no re-fetch) ──
  /* W-025: `trigger` names what asked for the recalculation. A throw here used to ESCAPE: from the cash sweep settings handler (an
     event handler) no error boundary could catch it, so the page kept showing the PREVIOUS numbers with no warning; from the balance
     sheet assumptions updater it reached the provider boundary as a generic page error. Now it is recorded as a compute failure and
     every model page shows ModelLoadError instead of stale numbers. The engine call and its arguments are unchanged. */
  const runOrchestrator = useCallback((cut, merged, bsAssum, swSet, trigger) => {
    const raw = rawRef.current;
    // ★ B3a (A4): honest-null, was Semitek's 'chase_money_market'.
    const sweepKey = raw.sweepAccountKey || null;
    // ★ B3a (A3, dead site): was `|| "2023-12"` — Semitek's window edge. Not reached (the
    // caller always passes a value, verified in diffs/sweep-b3-verify.txt step 0); the
    // literal is removed so a genuinely-null date propagates honestly.
    const openingMonth = raw.openingMonth || null;
    const openingMMBalance = (raw.dec23?.[sweepKey]) || (raw.bsActuals?.[sweepKey]?.[openingMonth]) || 0;
    let result;
    try {
    result = runModel({
      cutoff: cut,
      actualsData: raw.isActuals,
      forecastData: merged || raw.mergedForecast,
      bsActuals: raw.bsActuals,
      bsForecast: raw.bsForecast,
      bsAssumptions: bsAssum,
      dec23: raw.dec23,
      openingMMBalance,
      allMonths: raw.allMonths,
      openingMonth,
      sweepAct: raw.sweepAct,
      oneoffs: raw.oneoffs,
      finZero: raw.finZero,
      sweepSettings: swSet,
      daResult: raw.daResult,
      vendorOpex: raw.vendorOpex,
      prepaidBalance: raw.prepaidBalance,
      defRevResult: raw.defRevResult,
      fundraisingRounds: raw.fundraisingRounds || [],
      aeQuotaDR: raw.aeQuotaDR || null,
      companyQuotaDR: raw.cqSettings?.enabled ? (raw.companyQuotaDR || null) : null,
      miscRevenueLines: raw.miscRevenueLines || [],
      implementationRevenue: raw.implementationRevenue || {},
      implementationLines: raw.implementationLines || [],
      /* ★ PART D — THE LENGTH-REFUSAL RECORD, CARRIED SO A SCREEN CAN RENDER IT.
         Part B computes the refusal and runFullModel projects it as implLengthWarnings; until
         now NOTHING read it, so a deferred line with no length was excluded SILENTLY — the
         exact §E1 failure the fail-loud exists to prevent. A fail-loud that reaches nobody is
         not a fail-loud. The Revenue Build implementation editor is its reader. */
      implLengthWarnings: raw.implLengthWarnings || [],
      isLines: raw.isLines,
      bsLines: raw.bsLines,
      cfsLines: raw.cfsLines,
      revMappings: raw.revMappings,
      /* ★ The config-change re-run calls runModel DIRECTLY, so it needs the key too — omit
         it here and a BS-assumption or cutoff change would silently revert the row to the
         two-row shape until the next full fetch. */
      sweepInterestLineKey: raw.sweepInterestLineKey || null,
      /* Bi-weekly payroll, step 3b -- THE SECOND RUN PATH (CLAUDE.md 9.5). This re-run calls runModel DIRECTLY, so it must hand
         over the same accrued-payroll balance runFullModel did. Omit it and the linked account would drop to 0 the first time
         an operator changed a cash-sweep setting or a balance-sheet assumption -- silently, with the balance sheet balanced. */
      accruedPayroll: raw.accruedPayroll,
      accruedCommissions: raw.accruedCommissions,   // commission ship 4: the same second-run rule (the linked account would drop to 0)
    });
    } catch (err) {
      console.error('ModelProvider: recalculation failed', err);
      setModelState('error');
      setModelError(err && err.message ? err.message : String(err));
      setModelErrorInfo({ kind: 'compute', trigger: trigger || null, at: new Date().toISOString() });
      return;
    }
    setModelErrorInfo(null);
    setIsData(result.isData);
    setBsData(result.bsData);
    setCfsData(result.cfsData);
    setSwData(result.swData);
    setIterations(result.iterations);
    setConverged(result.converged);
    setFrLines(result.frLines || []);
    setImplDeferredLines(result.implDeferredLines || []);
    // ★ Item-5 READER — keep the gap consistent on a config re-run (orchestrator-only;
    // no missingCloseMonth at this level). A re-run runs on an already-loaded model, so
    // this is normally null; derived READ-ONLY from the result the orchestrator returns.
    const gapVal = result.missingChartOfAccounts?.length ? { kind: 'no_chart_of_accounts' } : null;
    setOnboardingGap(gapVal);
    // ★ CRASHROOT — a config re-run writes the gap, so it must write the state beside
    // it or the two disagree and a gapped model would render as 'ready'.
    setModelState(gapVal ? 'empty' : 'ready');
  }, []);

  // ── Config change handlers (trigger immediate re-run, no re-fetch) ──
  const handleSetCutoff = useCallback((newCutoff) => {
    setCutoff(newCutoff);
    if (initialized) runOrchestrator(newCutoff, null, bsAssumptions, sweepSettings, 'recalculating after the last closed month changed');
  }, [initialized, bsAssumptions, sweepSettings, runOrchestrator]);

  /* W-053 step 3b -- DEFENCE IN DEPTH (diffs/w053-3b-phase0.txt section 2). For a view-only login the two persisting config setters
     do NOTHING: no state change, no model re-run, no save. Before this, a viewer's edit re-ran the WHOLE MODEL on screen and the save
     then failed (the sweep's silently: .catch(() => {})), so every statement showed a model the server never stored. The page greys
     these controls too (ViewOnly); this guard holds even if a control is ever left outside a region. */
  const handleSetBsAssumptions = useCallback((updater) => {
    if (!canEditModel) return;
    setBsAssumptions(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (initialized) runOrchestrator(cutoff, null, next, sweepSettings, 'recalculating after the balance sheet assumptions changed');
      return next;
    });
  }, [initialized, cutoff, sweepSettings, runOrchestrator, canEditModel]);

  const handleSetSweepSettings = useCallback((newSS) => {
    if (!canEditModel) return;   // W-053 3b: see handleSetBsAssumptions above
    setSweepSettings(newSS);
    if (initialized) runOrchestrator(cutoff, null, bsAssumptions, newSS, 'recalculating after the cash sweep settings changed');
    // Persist to companies (min_cash / sweep_rate / sweep_mround). Fire-and-forget — the engine
    // already re-ran above with newSS, so a slow/failed save doesn't block the UI; on the next
    // full fetch the DB value seeds sweepSettings (DB = source of truth). The UI setters always
    // spread the full {minCash,rate,mround}, so all three are present every call.
    updateCompanySettings(companyId, {
      min_cash: newSS.minCash,
      sweep_rate: newSS.rate,
      sweep_mround: newSS.mround,
    }).catch(() => {});
  }, [initialized, cutoff, bsAssumptions, companyId, runOrchestrator, canEditModel]);

  // ── Stale/fresh control ──
  const markStale = useCallback(() => { staleRef.current = true; }, []);

  const ensureFresh = useCallback(async () => {
    // ★ SWITCH-RACE FIX, gate 1: never compute with an un-seeded cutoff. staleRef is
    // deliberately left TRUE, so the page's effect (which re-fires when the seed
    // changes cutoff/sweepSettings, and again when `initialized` flips) retries once
    // seeding has resolved. This is what stops the null-cutoff run entirely.
    if (!cutoffSeededRef.current) return;
    // ★ gate 2: the initial-load compute owns the mount — do not start a second,
    // redundant run alongside it.
    if (inFlightRef.current) return;
    if (!staleRef.current && initialized) return;
    await fetchAndCompute({ cut: cutoff, bsAssum: bsAssumptions, swSet: sweepSettings });
  }, [cutoff, bsAssumptions, sweepSettings, fetchAndCompute, initialized]);

  const refresh = useCallback(async () => {
    // ★ Same seed gate: an explicit refresh before the cutoff is seeded would
    // reintroduce the null-cutoff compute this fix removes. (No page calls refresh()
    // today — it is exposed on context only — but the gate keeps the invariant true
    // for every entry point, not just the one that happened to have the bug.)
    if (!cutoffSeededRef.current) return;
    staleRef.current = true;
    await fetchAndCompute({ cut: cutoff, bsAssum: bsAssumptions, swSet: sweepSettings });
  }, [cutoff, bsAssumptions, sweepSettings, fetchAndCompute]);

  // ── Initial load ──
  useEffect(() => {
    if (companyId && !initialized) {
      (async () => {
        // CHANGE B (Session 5a Item 1): source initial cutoff from
        // companies.close_month rather than the React useState default
        // ("2025-12"). Set-once-per-mount — ModelProvider is keyed on
        // companyId in App.js so a company switch remounts and re-runs
        // this effect. NULL close_month tenants (cid=1 Semitek / cid=2
        // Chris Test today) fall back to the existing React default.
        // Subsequent close-month advances via future monthly close
        // workflow require operator page reload (§12 entry tracks this).
        // Uses a local `initialCutoff` for the immediate fetchAndCompute
        // call because setCutoff is async — the React state won't be
        // updated by the time the call line runs. setCutoff is still
        // called so subsequent re-runs (BS assumptions / sweep edits)
        // pick up the right value.
        let initialCutoff = cutoff;
        // Seed sweepSettings from the DB (companies.min_cash / sweep_rate / sweep_mround) so the
        // persisted per-tenant values survive reload, instead of always resetting to the hardcoded
        // {minCash:250000, rate:0.038, mround:'none'} default. Null columns fall back to that
        // default (safety). Raw setSweepSettings (NOT handleSetSweepSettings) — seeding must not
        // re-persist or re-run; the initial fetchAndCompute below runs the engine with swSet.
        let initialSweep = sweepSettings;
        try {
          const res = await getCompanies();
          const c = (res.data || []).find(c => c.id === companyId);
          if (c) {
            if (c.close_month) { initialCutoff = c.close_month; setCutoff(c.close_month); }
            initialSweep = {
              minCash: c.min_cash != null ? Number(c.min_cash) : sweepSettings.minCash,
              rate:    c.sweep_rate != null ? Number(c.sweep_rate) : sweepSettings.rate,
              mround:  c.sweep_mround != null ? c.sweep_mround : sweepSettings.mround,
            };
            setSweepSettings(initialSweep);
          }
        } catch (e) { /* leave fallback */ }
        // ★ SWITCH-RACE FIX: the seed attempt has RESOLVED — set unconditionally, so a
        // tenant whose close_month is genuinely null (or whose getCompanies threw) still
        // proceeds to compute and reaches the named missingCloseMonth state rather than
        // waiting forever for a seed that is never coming.
        cutoffSeededRef.current = true;
        fetchAndCompute({ cut: initialCutoff, bsAssum: bsAssumptions, swSet: initialSweep });
      })();
    }
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Golden-snapshot capture (window trigger) ──
  // Keep in place across refactor phases so we can re-capture and diff.
  // Trigger from DevTools console (either works, both survive React Router):
  //   window.__CAPTURE_SNAPSHOT__ = true   // property setter fires immediately
  //   window.captureSnapshot()             // direct call
  const capturedRef = useRef(false);
  useEffect(() => {
    if (!initialized) return;

    const doCapture = () => {
      if (capturedRef.current) return;
      capturedRef.current = true;
      const snapshot = {
        capturedAt: new Date().toISOString(),
        companyId,
        cutoff,
        result: {
          isData, bsData, cfsData, swData,
          iterations, converged,
          frLines, implDeferredLines,
          aeQuotaDR, companyQuotaDR,
          miscRevenue, miscRevenueLines,
          implementationRevenue, implementationLines,
          payrollData, payrollHC, contractorHC,
          rawData: rawRef.current,
        },
      };
      const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GOLDEN_SNAPSHOT_OUTPUTS_${companyId}_${cutoff}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // Allow re-triggering after a short delay (e.g. after switching companies)
      setTimeout(() => { capturedRef.current = false; }, 500);
    };

    window.captureSnapshot = doCapture;

    // Property setter so `window.__CAPTURE_SNAPSHOT__ = true` fires synchronously.
    // Wrapped in try/catch in case the property was already defined on a prior render.
    try {
      Object.defineProperty(window, '__CAPTURE_SNAPSHOT__', {
        configurable: true,
        get: () => false,
        set: (v) => { if (v === true) doCapture(); },
      });
    } catch (e) { /* already defined with incompatible descriptor — ignore */ }
  });

  const value = {
    // Model outputs
    isData, bsData, cfsData, swData, iterations, converged, payrollHC, payrollData, contractorHC, frLines, implDeferredLines, aeQuotaDR, companyQuotaDR, miscRevenue, miscRevenueLines, implementationRevenue, implementationLines,
    // Model config
    cutoff, setCutoff: handleSetCutoff,
    bsAssumptions, setBsAssumptions: handleSetBsAssumptions,
    sweepSettings, setSweepSettings: handleSetSweepSettings,
    // Control
    loading, initialized, markStale, ensureFresh, refresh,
    // ★ CRASHROOT PART 1 — ADDITIVE. `loading` and `initialized` keep their exact
    // meanings; modelState is the single terminal-exit answer new readers should use.
    modelState, modelError,
    // W-025: why it failed, and the page / company to name in the details block.
    modelErrorInfo, errorContext: { page: pageLabel || null, company: companyName || null },
    // Item-5 READER — whole-model empty-state gap (null when clear)
    onboardingGap,
    // 9d: the pre-flight verdict (still REPORT-ONLY here; only the Go Live gate reads it)
    preflight,
    // Identity (Phase 6: BS assumptions panel needs companyId for API saves)
    companyId,
    // W-053 3b: may this login change the model? (false = view only; pages grey their edit regions with components/ViewOnly)
    canEditModel,
    // Commission ship 5 (b): is this login the super admin? (display only -- e.g. the Payroll page's "Commissions: Not answered")
    superAdmin,
    // Raw data access (for pages that need underlying data, e.g. BS assumptions panel)
    rawData: rawRef.current,
  };

  return <ModelContext.Provider value={value}>{children}</ModelContext.Provider>;
}
